import React, { useState } from "react";
import SumsubWebSdk from "@sumsub/websdk-react";
import { SumsubVerifyButton } from "./SumsubVerifyButton";
import { useSelectedVerificationAttempt } from "@/features/account/hooks/useVerifications";
import { Alert } from "@picklebet/ui";
import { Button, PageLoader } from "@picklebet/ui";
import { AnimatePresence, motion } from "motion/react";
import { VerificationError } from "../VerificationError";
import { buildVerificationErrorInfo } from "@/features/account/utils/verificationError";
import type { VerificationErrorInfo } from "@/features/account/types/VerificationError";
import { SupportLink } from "@/features/shell/components/shared/SupportLink";
import { usePreviousValue } from "@picklebet/ui";
import { parseDate } from "utilities/datetime";

export const Sumsub = () => {
  const { attempt } = useSelectedVerificationAttempt();
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [hasExpired, setHasExpired] = useState(false);
  const [widgetError, setWidgetError] = useState<VerificationErrorInfo | null>(
    null,
  );
  const [remountKey, setRemountKey] = useState(0);

  const previousUpdatedAt = usePreviousValue(attempt?.updatedAt);
  const previousDestination = usePreviousValue(attempt?.destination);

  const parsedAttemptUpdatedAt = parseDate(attempt?.updatedAt);
  const parsedPreviousAttemptUpdatedAt = parseDate(previousUpdatedAt);

  const isMidVerificationFlow =
    attempt?.attemptStatus === "REJECTED_FOR_RESUBMIT" &&
    parsedAttemptUpdatedAt &&
    parsedPreviousAttemptUpdatedAt &&
    attempt?.destination &&
    previousDestination &&
    parsedAttemptUpdatedAt > parsedPreviousAttemptUpdatedAt &&
    attempt.destination === previousDestination;

  const handleExpired = async () => {
    setHasExpired(true);
  };

  const handleError = (error: unknown) => {
    console.error("[Sumsub] SDK error", error);

    setWidgetError(
      buildVerificationErrorInfo({ source: "widget", cause: error }),
    );
  };

  const handleRetry = () => {
    setWidgetError(null);
    setWidgetLoaded(false);
    setRemountKey((k) => k + 1);
  };

  const handleMessage = (event: string, payload: Record<string, any>) => {
    console.log("[Sumsub] message", event, payload);

    switch (event) {
      case "idCheck.onInitialized": {
        setWidgetLoaded(true);
        return;
      }
      default:
        break;
    }
  };

  // Attempt data hasn't arrived from Firestore yet - the parent skeleton covers
  // this gap, so render nothing rather than briefly flashing a PageLoader.
  if (!attempt) return null;

  if (widgetError) {
    return (
      <VerificationError
        error={widgetError}
        variant="inline"
        title="Verification couldn't load"
        actions={
          <Button onClick={handleRetry} size="md" variant="danger">
            Try again
          </Button>
        }
      />
    );
  }

  // Show the verify button when the attempt has loaded but has no token or the
  // session has expired. We also gate on the token while mid-flow: the SDK only
  // initializes once (it ignores later accessToken prop changes), so mounting
  // it with an empty token permanently loses the dashboard customization.
  if (!attempt.accessToken || (!isMidVerificationFlow && hasExpired)) {
    return attempt.destination ? (
      <>
        {hasExpired ? (
          <Alert color="warning" title="Session expired">
            Your verification session has expired, please click the button below
            to continue.
          </Alert>
        ) : attempt.attemptStatus === "REJECTED_FOR_RESUBMIT" ? (
          <Alert color="danger" title={"Attempt Failed"}>
            <div>
              We failed to verify you from your previous attempt. Please click
              the button below to try again. If you continue to have issues,
              please contact{" "}
              <SupportLink
                hideChevron
                options={{ labels: ["weblink-contact-support"] }}
              >
                Support
              </SupportLink>
            </div>
          </Alert>
        ) : null}
        <SumsubVerifyButton
          attemptId={attempt.attemptId}
          destination={attempt.destination}
          onSuccess={() => {
            setHasExpired(false);
          }}
        />
      </>
      // Should be hard to get here with a valid sumsub attempt, but putting this here to solve Typescript error
    ) : (
      <Alert
        color="warning"
        title="Verification outdated"
        actions={
          <Button
            onClick={() => window.location.reload()}
            variant="warning"
            size="md"
          >
            Reload
          </Button>
        }
      >
        We ran into an issue loading your verification, if this persists please
        contact{" "}
        <SupportLink
          hideChevron
          options={{ labels: ["weblink-contact-support"] }}
        >
          Support{" "}
        </SupportLink>
      </Alert>
    );
  }

  console.log("[Sumsub] mounting widget");
  console.table({
    remountKey,
    hasAccessToken: Boolean(attempt.accessToken),
    accessTokenLength: attempt.accessToken?.length ?? 0,
    attemptId: attempt.attemptId,
    destination: attempt.destination,
    attemptStatus: attempt.attemptStatus,
    isMidVerificationFlow,
    widgetLoaded,
  });

  return (
    <div style={{ position: "relative" }}>
      <AnimatePresence>
        {!widgetLoaded && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: "absolute", inset: 0 }}
            transition={{ duration: 0.3 }}
          >
            <PageLoader text="Loading verification..." />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: widgetLoaded ? 1 : 0 }}
        transition={{ duration: 0.5 }}
      >
        <SumsubWebSdk
          key={`${remountKey}-${attempt.accessToken}`}
          accessToken={attempt.accessToken}
          options={{ enableScrollIntoView: false }}
          expirationHandler={handleExpired}
          onMessage={handleMessage}
          onError={handleError}
        />
      </motion.div>
    </div>
  );
};
